import { useEffect, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { useEffectAfterMount } from "@/Common/CustomHooks/useEffectAfterMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { PharmacyOrderStatus } from "@/Common/Models/UI/Enums";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { PharmacyOrderPrescriptionType } from "../Common/PharmacyOrderPrescriptionType";
import { PharmacyOrdersFilterSettingsModel } from "../Models/PharmacyOrdersFilterSettingsModel";

import styles from "./PharmacyOrdersFilterSettings.module.scss";

interface PharmacyOrdersFilterSettingsProps {
    cachedSettings: PharmacyOrdersFilterSettingsModel;
    onSaveChanges: (settings: PharmacyOrdersFilterSettingsModel, isSaveSettingsChecked: boolean) => void;
    onClose: () => void;
    onColumnOrderingPopupOpenClick: () => void;
}

function PharmacyOrdersFilterSettings({ cachedSettings, onSaveChanges, onClose, onColumnOrderingPopupOpenClick }: Readonly<PharmacyOrdersFilterSettingsProps>): JSX.Element {
    const { getText } = useTranslation();
    const [pharmacyOrderSettings, setPharmacyOrderSettings] = useState<PharmacyOrdersFilterSettingsModel>(cachedSettings ?? new PharmacyOrdersFilterSettingsModel());
    const [isSaveSettingsChecked, setIsSaveSettingsChecked] = useState<boolean>(false);
    const [pharmacyOrderStatus, setPharmacyOrderStatus] = useState<CheckboxUIModel[]>(
        () => EnumHelper
            .getNamesAndValues(PharmacyOrderStatus)
            .map(
                p => new CheckboxUIModel({
                    Id: String(p.value),
                    Name: getText(`Enums_PharmacyOrderStatus_${p.name}`),
                    Checked: pharmacyOrderSettings.selectedOrderStatus.includes(p.value)
                })
            )
    );

    const [pharmacyOrderPrescriptionType, setPharmacyOrderPrescriptionType] = useState<CheckboxUIModel[]>(
        () => EnumHelper
            .getNamesAndValues(PharmacyOrderPrescriptionType)
            .map(
                p => new CheckboxUIModel({
                    Id: String(p.value),
                    Name: getText(`Enums_PharmacyOrderPrescriptionType_${p.name}`),
                    Checked: pharmacyOrderSettings.selectedPrescriptionType.includes(p.value)
                })
            )
    );

    useEffect(() => {
        const overlayKey = "PharmacyOrdersFilterSettingsOverlay";

        overlayStore.showOverlay(overlayKey, {
            transparent: true,
            onClicked: () => {
                onClose();
            }
        });

        return () => {
            overlayStore.hideOverlay(overlayKey);
        };
    }, [onClose]);

    useEffectAfterMount(() => {
        setPharmacyOrderSettings(
            {
                ...pharmacyOrderSettings,
                selectedOrderStatus: pharmacyOrderStatus
                    .reduce((selected, status) => status.Checked ? [...selected, EnumHelper.getValue(PharmacyOrderStatus, status.Id)] : selected, [])
            }
        );
    }, [pharmacyOrderStatus]);

    useEffectAfterMount(() => {
        setPharmacyOrderSettings(
            {
                ...pharmacyOrderSettings,
                selectedPrescriptionType: pharmacyOrderPrescriptionType
                    .reduce((selected, type) => type.Checked ? [...selected, EnumHelper.getValue(PharmacyOrderPrescriptionType, type.Id)] : selected, [])
            }
        );
    }, [pharmacyOrderPrescriptionType]);

    return (
        <div className={styles.filterSettingsContainer}>
            <div className={styles.headers}>
                <div className={styles.orderLabelMinWidth} data-cy="pharmacy-orders-order-label">
                    {getText("PharmacyOrders_OrderLabel").toUpperCase()}
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("PharmacyOrders_OrderStatusLabel").toUpperCase()}
                        checked={pharmacyOrderStatus.every(p => p.Checked)}
                        onChange={isChecked => setPharmacyOrderStatus(p => p.map(status => new CheckboxUIModel({ ...status, Checked: isChecked })))}
                        dataCy="select-all-order-status-checkbox"
                    />
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("PharmacyOrders_PrescriptionLabel").toUpperCase()}
                        checked={pharmacyOrderPrescriptionType.every(p => p.Checked)}
                        onChange={isChecked => {
                            setPharmacyOrderPrescriptionType(p => p.map(type => new CheckboxUIModel({ ...type, Checked: isChecked })));
                        }}
                        dataCy="select-all-order-prescription-type-checkbox"
                    />
                </div>
                <div className={styles.settingContainer} data-cy="pharmacy-orders-settings-label">
                    {getText("SettingsLabel").toUpperCase()}
                </div>
            </div>
            <div className={styles.columns}>
                <div className="date">
                    <div className={styles.datesLabels} data-cy="pharmacy-orders-preferred-date-label">
                        {getText("PharmacyOrders_PreferredDate")}
                    </div>
                    <MultiDateSelector
                        dataCy="pharmacy-orders-preferred-date-selector"
                        id="PharmacyOrderPreferredDates"
                        from={pharmacyOrderSettings?.preferredDateFrom}
                        to={pharmacyOrderSettings?.preferredDateTo}
                        fromSetTodayLabel={getText("TodayLabel")}
                        toSetTodayLabel={getText("TodayLabel")}
                        fromChange={date => setPharmacyOrderSettings({ ...pharmacyOrderSettings, preferredDateFrom: date })}
                        toChange={date => setPharmacyOrderSettings({ ...pharmacyOrderSettings, preferredDateTo: date })}
                    />
                    <div className={styles.datesLabels} data-cy="pharmacy-orders-received-date-label">
                        {getText("PharmacyOrders_ReceivedDate")}
                    </div>
                    <MultiDateSelector
                        dataCy="pharmacy-orders-received-date-selector"
                        id="PharmacyOrderReceivedDates"
                        from={pharmacyOrderSettings?.receivedDateFrom}
                        to={pharmacyOrderSettings?.receivedDateTo}
                        fromSetTodayLabel={getText("TodayLabel")}
                        toSetTodayLabel={getText("TodayLabel")}
                        fromChange={date => setPharmacyOrderSettings({ ...pharmacyOrderSettings, receivedDateFrom: date })}
                        toChange={date => setPharmacyOrderSettings({ ...pharmacyOrderSettings, receivedDateTo: date })}
                    />
                </div>
                <div className={styles.elements}>
                    <CheckedListControl
                        dataCy="pharmacy-order-settings-status-list"
                        DataSource={Immutable.List(pharmacyOrderStatus)}
                        onChange={(isChecked: boolean, checkboxId: string) =>
                            setPharmacyOrderStatus(p => p.map(status => status.Id === checkboxId ? new CheckboxUIModel({ ...status, Checked: isChecked }) : status))
                        }
                        idModifier="pharmacyOrderStatus"
                    />
                </div>
                <div className={styles.elements}>
                    <CheckedListControl
                        dataCy="pharmacy-order-settings-prescription-type-list"
                        DataSource={Immutable.List(pharmacyOrderPrescriptionType)}
                        onChange={(isChecked: boolean, checkboxId: string) =>
                            setPharmacyOrderPrescriptionType(p => p.map(type => type.Id === checkboxId ? new CheckboxUIModel({ ...type, Checked: isChecked }) : type))
                        }
                        idModifier="pharmacyOrderPrescriptionType"
                    />
                </div>
                <div className={`${styles.elements} ${styles.settingContainer}`}>
                    <CheckboxControl
                        dataCy="pharmacy-order-settings-include-archived"
                        id="IncludeArchivedId"
                        label={getText("PharmacyOrders_IncludeArchivedLabel")}
                        checked={pharmacyOrderSettings?.includeArchived}
                        onChange={isChecked => setPharmacyOrderSettings({ ...pharmacyOrderSettings, includeArchived: isChecked })}
                    />
                    <CheckboxControl
                        dataCy="pharmacy-order-settings-include-filed-orders"
                        id="IncludeFiledOrdersCheckboxControl"
                        label={getText("PharmacyOrders_IncludeFiledOrdersLabel")}
                        checked={pharmacyOrderSettings?.includeFiledAndContactedOrders}
                        onChange={isChecked => setPharmacyOrderSettings({ ...pharmacyOrderSettings, includeFiledAndContactedOrders: isChecked })}
                    />
                </div>
            </div>
            <div className={styles.footer} data-cy="pharmacy-order-settings-footer">
                <SearchSettingsFooter
                    onClose={() => onClose()}
                    onOk={() => onSaveChanges(pharmacyOrderSettings, isSaveSettingsChecked)}
                    onColumnOrderingButtonClick={() => onColumnOrderingPopupOpenClick()}
                    onResultsPerPageChange={pageSize => setPharmacyOrderSettings({ ...pharmacyOrderSettings, queryPageSize: pageSize })}
                    selectedPageSizeOption={pharmacyOrderSettings?.queryPageSize}
                    onSaveSettingsCheckboxChange={checked => setIsSaveSettingsChecked(checked)}
                    isSaveSettingsChecked={isSaveSettingsChecked}
                    pageSizeOptions={[30, 50, 100]}
                />
            </div>
        </div>
    );
}

export { PharmacyOrdersFilterSettings };
